/**
 * Número, dinheiro e data em português do Brasil — num lugar só.
 *
 * Existe como módulo central por um motivo medido em outro projeto desta
 * máquina: quando cada tela formata do seu jeito, a mesma grandeza aparece
 * como `1234.5` numa tabela e `1.234,50` na tela ao lado, e o usuário conclui
 * que são dois números diferentes. Aqui, quem quiser mostrar número importa
 * daqui ou está errado.
 *
 * Duas decisões que não são óbvias:
 *
 * **A leitura aceita vírgula e ponto.** O produtor digita `2,5` no celular e
 * `2.5` no teclado numérico do notebook, às vezes na mesma sessão. Recusar um
 * dos dois é transformar hábito de teclado em erro de validação. O que NÃO se
 * aceita é adivinhar errado: `1.234` em campo brasileiro é mil duzentos e
 * trinta e quatro, não um vírgula dois.
 *
 * **Dinheiro aqui é float, de propósito.** A regra de usar inteiro em centavos
 * vale para sistema que soma lançamento e fecha caixa; errar o arredondamento
 * lá vira diferença de saldo. Este app não movimenta dinheiro: ele estima
 * economia anual a partir de um BMP que já tem ±25% de incerteza. Centavo
 * exato sobre estimativa de dezena de milhar é precisão falsa, e o custo de
 * carregar BigInt até a tela seria pago sem comprar nada.
 */

const LOCAL = 'pt-BR';
const FUSO = 'America/Sao_Paulo';

/** `Intl.NumberFormat` é caro de construir e é chamado em laço de render. */
const cacheDeFormato = new Map();

function formatador(opcoes) {
  const chave = JSON.stringify(opcoes);
  let f = cacheDeFormato.get(chave);
  if (!f) {
    f = new Intl.NumberFormat(LOCAL, opcoes);
    cacheDeFormato.set(chave, f);
  }
  return f;
}

function finito(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * Número com casas decimais fixas: `numero(1234.5, 2)` → `"1.234,50"`.
 *
 * `null` e `undefined` viram travessão em vez de `"0"` ou `"NaN"`. Essa é a
 * regra do app inteiro: falta de dado não é zero. Um `0` na tela é uma medição
 * que deu zero, e o produtor tem o direito de distinguir as duas coisas.
 */
export function numero(valor, casas = 1) {
  if (!finito(valor)) return '—';
  return formatador({
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(valor);
}

/** Sem casas decimais, com separador de milhar: `129484` → `"129.484"`. */
export function inteiro(valor) {
  if (!finito(valor)) return '—';
  return formatador({ maximumFractionDigits: 0 }).format(valor);
}

/**
 * Casas decimais conforme o tamanho do número.
 *
 * Quem lê não quer `0,3 toneladas` nem `96.399,45 metros cúbicos`: quer a
 * precisão que faz diferença na decisão dele. Abaixo de 1, três casas; abaixo
 * de 100, uma; acima disso, nenhuma — a terceira casa de um número de cinco
 * dígitos é ruído com aparência de rigor.
 */
export function expressivo(valor) {
  if (!finito(valor)) return '—';
  const m = Math.abs(valor);
  if (m === 0) return '0';
  if (m < 1) return numero(valor, 3);
  if (m < 100) return numero(valor, 1);
  return inteiro(valor);
}

/**
 * O número como a fonte o escreveu: `7` → `"7"`, `7.5` → `"7,5"`.
 *
 * Existe para faixa de literatura. `numero(7, 1)` devolve `"7,0"` e inventa
 * uma precisão que o artigo não tinha — e, na mesma tela, a ressalva vinda da
 * API dizia "de 7 a 15" enquanto a dica do campo dizia "de 7,0 a 15,0". Duas
 * escritas da mesma faixa a dois centímetros uma da outra. `_n` no
 * `api/app/rotas/simulacao.py` aplica esta mesma regra do lado de lá.
 */
export function enxuto(valor, casas = 1) {
  if (!finito(valor)) return '—';
  return formatador({ maximumFractionDigits: casas }).format(valor);
}

/**
 * Potência na unidade em que ela se lê: `0.405` → `{ valor: '405', unidade: 'W' }`.
 *
 * Devolve os dois pedaços separados porque o cartão do resultado desenha o
 * número grande e a unidade pequena ao lado — juntá-los num texto só obrigaria
 * a tela a separar de novo.
 *
 * Abaixo de 1 kW a regra geral daria `0,405 kW`: três casas de precisão falsa
 * num número que carrega ±25% de incerteza e, pior, uma ordem de grandeza
 * escondida. 405 W é menos que um chuveiro, e o produtor precisa enxergar isso
 * para entender por que o app diz que não existe gerador desse tamanho. É a
 * mesma troca que o motor faz com `massa_t` (tonelada → quilo) e com
 * `potencia`; as duas pontas têm de arredondar igual.
 */
export function potencia(kw) {
  if (!finito(kw)) return { valor: '—', unidade: 'kW' };
  if (Math.abs(kw) < 1) return { valor: inteiro(kw * 1000), unidade: 'W' };
  return { valor: expressivo(kw), unidade: 'kW' };
}

/** `116535.37` → `"R$ 116.535"`. Sem centavo: ver o cabeçalho do módulo. */
export function dinheiro(valor) {
  if (!finito(valor)) return '—';
  return formatador({
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(valor);
}

/** Com centavos — para preço unitário (`R$ 0,90/kWh`), não para total anual. */
export function dinheiroExato(valor) {
  if (!finito(valor)) return '—';
  return formatador({
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor);
}

/** `66.08` → `"66%"`. Porcentagem já vem em pontos percentuais, não em fração. */
export function porcento(valor, casas = 0) {
  if (!finito(valor)) return '—';
  return `${numero(valor, casas)}%`;
}

/**
 * Texto digitado → número, aceitando os dois jeitos de escrever.
 *
 * Devolve `null` quando não dá para ler. `null` aqui significa "campo vazio ou
 * ilegível", nunca `0` — quem chama precisa dessa diferença para decidir entre
 * "não preencheu" e "preencheu zero".
 *
 * A regra do separador, em ordem:
 *
 * 1. Tem vírgula e ponto → o último dos dois é o decimal. `1.234,56` e
 *    `1,234.56` saem ambos certos.
 * 2. Só vírgula → decimal. É o teclado brasileiro.
 * 3. Só ponto, e os pontos separam grupos de exatamente três dígitos com algo
 *    antes → milhar. `150.000` é cento e cinquenta mil, que é o que o produtor
 *    quis dizer ao digitar cabeças de frango.
 * 4. Só ponto, qualquer outro caso → decimal. `2.5` é dois e meio.
 *
 * O caso 3 é o único que adivinha, e adivinha a favor de quem digita rápido: o
 * erro oposto (ler `150.000` cabeças como `150`) subestimaria a granja em mil
 * vezes e passaria despercebido, porque 150 cabeças também é um número
 * plausível.
 */
export function lerNumero(texto) {
  if (typeof texto === 'number') return finito(texto) ? texto : null;
  if (typeof texto !== 'string') return null;

  const limpo = texto
    .replace(/\s/g, '')
    .replace(/R\$/gi, '')
    .replace(/[^\d,.\-+]/g, '');
  if (!limpo || !/\d/.test(limpo)) return null;

  const temVirgula = limpo.includes(',');
  const temPonto = limpo.includes('.');
  let normalizado;

  if (temVirgula && temPonto) {
    const decimal = limpo.lastIndexOf(',') > limpo.lastIndexOf('.') ? ',' : '.';
    const milhar = decimal === ',' ? '.' : ',';
    normalizado = limpo.split(milhar).join('').replace(decimal, '.');
  } else if (temVirgula) {
    normalizado = limpo.split(',').join('.');
    // Mais de uma vírgula só faz sentido como separador de milhar: `1,234,567`.
    if ((limpo.match(/,/g) || []).length > 1) normalizado = limpo.split(',').join('');
  } else if (temPonto && /^[+-]?\d{1,3}(\.\d{3})+$/.test(limpo)) {
    normalizado = limpo.split('.').join('');
  } else {
    normalizado = limpo;
  }

  const n = Number(normalizado);
  return Number.isFinite(n) ? n : null;
}

/**
 * Data no fuso de São Paulo.
 *
 * O fuso vai explícito porque o servidor roda em UTC e o celular do produtor
 * não. Sem isso, um estudo salvo às 22h de quinta aparece como sexta-feira no
 * relatório — e data errada em documento que vai ao banco é problema real.
 */
export function data(iso, comHora = false) {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCAL, {
    timeZone: FUSO,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(comHora ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(d);
}

/** `"2000"` → `"2.000 animais"`, `"1"` → `"1 animal"`. */
export function plural(quantidade, singular, pluralForma) {
  const q = finito(quantidade) ? quantidade : 0;
  return `${inteiro(q)} ${q === 1 ? singular : pluralForma}`;
}
